import { mutationOptions, queryOptions } from '@tanstack/react-query'
import {
  NotificationSettingListModel,
  NotificationSettingListResponse,
} from 'src/models/notificationSetting.models'
import { QueryError } from 'src/queries/network.utils'
import { NotificationSettingsResponse } from 'src/queries/notificationSettings/notificationSettings.models'
import {
  getNotificationSettings,
  patchNotificationSettings,
} from 'src/queries/notificationSettings/notificationSettings.network'
import { PatchNotificationSettingsRequest } from 'src/requests/patchNotificationSettings.request'

/**
 * User notification settings (`/notificationsettings`).
 *
 * NOTE: the root key is deliberately 'notificationsettings'. It is a
 * DIFFERENT family from `notificationsQueryKeys` ('notifications') in
 * `src/queries/queries.constants.ts`, which keys the activity notifications
 * list. No existing cross-feature invalidation or interop entry targets
 * notification settings, so the keys are colocated here per the README.
 */
export const notificationSettingsQueryKeys = {
  all: () => ['notificationsettings'] as const,
  list: () => [...notificationSettingsQueryKeys.all(), 'list'] as const,
}

// No explicit staleTime: app QueryClient defaults (staleTime 0 +
// refetchOnMount when a page is the only observer) reproduce the legacy
// behavior of dispatching the GET thunk on every page mount.
export const notificationSettingsQueries = {
  list: () =>
    queryOptions({
      queryKey: notificationSettingsQueryKeys.list(),
      queryFn: () => getNotificationSettings(),
    }),
}

export const notificationSettingsMutations = {
  patch: () =>
    mutationOptions<
      NotificationSettingsResponse,
      QueryError,
      PatchNotificationSettingsRequest
    >({
      mutationFn: (body) => patchNotificationSettings(body),
    }),
}

/**
 * Maps the raw list response to the `NotificationSettingListModel` class
 * instances the notification settings components consume. Intended for use
 * as a stable `select` option on `useQuery(notificationSettingsQueries.list())`.
 */
export const toNotificationSettingListModels = (
  response: NotificationSettingsResponse,
): NotificationSettingListModel[] =>
  response.notificationSettings.map(
    (setting) => new NotificationSettingListModel(setting),
  )

/**
 * Reproduces the legacy reducer's PATCH_NOTIFICATION_SETTINGS_SUCCESS
 * rendered behavior for `setQueryData`: settings already in the cached list
 * are replaced by their patched versions in place (order preserved); patched
 * ids not present in the cached list are ignored (the legacy reducer merged
 * them into `listResponses` but never added them to `order`, so they were
 * never rendered).
 *
 * Returns `undefined` when there is no cached list yet, which makes
 * `setQueryData` bail out instead of seeding the cache with a partial list.
 */
export const mergePatchedNotificationSettings = (
  existing: NotificationSettingsResponse | undefined,
  patched: NotificationSettingsResponse,
): NotificationSettingsResponse | undefined => {
  if (!existing) {
    return undefined
  }

  const patchedById = patched.notificationSettings.reduce(
    (acc, setting) => acc.set(setting.id, setting),
    new Map<string, NotificationSettingListResponse>(),
  )

  return {
    ...existing,
    notificationSettings: existing.notificationSettings.map(
      (setting) => patchedById.get(setting.id) ?? setting,
    ),
  }
}
